/**
 * The runtime (web-plan.md F1) — the web twin of `app/src/runtime/`: owns the
 * immutable vanilla store, the socket, and every async flow. Components send
 * commands (plain method calls); renderer adapters subscribe to snapshots.
 *
 * The one discipline ported wholesale from the desktop: every network
 * handler snapshots what it needs, awaits, then checks the account lifetime
 * and **re-checks `feed.channelId` before writing**. `appendMessages` dedupes on `seq`, and
 * `seq` is per channel — fifty of channel A's messages would splice silently
 * into B's history without the guard.
 */
import { freeze, produce } from "immer";
import { createStore, type StoreApi } from "zustand/vanilla";

import type { Message, SendBody, ServerEvent } from "../gen/dto";

import * as api from "../lib/api/rest";
import { UploadError, upload } from "../lib/api/uploads";
import { ChatSocket, type SocketEvent } from "../lib/api/ws";
import { type Dict, type Lang, detectLang, dict } from "../lib/i18n";
import {
  assertCurrent,
  currentLifetime,
  endLifetime,
  isCurrent,
  sessionDelay,
  type SessionLifetime,
} from "../lib/lifetime";
import { ORIGINAL_MAX_BYTES, photoWeights, photoWeightsSupported } from "../lib/photo";
import { armReauthWindow, inReauthWindow, session, setSession } from "../lib/session";
import * as sounds from "../lib/sounds";
import {
  VoiceSession,
  type VoiceEvents,
  shareViewSupported,
  voiceSupported,
} from "../lib/voice/session";
import { parseMarkdown, splitMessage } from "../lib/wasm";
import * as mut from "./mutators";
import {
  AFK_AFTER_MS,
  PAGE_SIZE,
  PENDING_SEQ,
  RING_FOR_MS,
  TICK_MS,
  WINDOW_SIZE,
  type UiState,
  initialState,
} from "./types";

const MAX_SPLIT_PARTS = 10;

/** Captured at Send, never reconstructed from whichever screen opens later. */
interface SendIntent {
  readonly lifetime: SessionLifetime;
  readonly accountId: number;
  readonly selfName: string;
  readonly channel: number;
  readonly replyTo: number | null;
  readonly editing: number | null;
  readonly parts: ReadonlyArray<{
    readonly text: string;
    readonly entities: import("../gen/dto").Entity[];
    readonly nonce: string;
  }>;
  readonly staged: ReadonlyArray<Readonly<StagedFile>>;
  readonly unlimited: boolean;
  readonly pause: number;
}

type SendContext = Omit<SendIntent, "parts" | "staged"> & {
  readonly nonce: string;
  readonly maxChars: number;
};

/** Finishing a recording may await an encoder; its target is already fixed. */
export type PreparedSend = (text: string, staged?: StagedFile[]) => void;

export type RuntimeTransitionKind = "screen" | "center" | "sheet" | "pop" | "tab" | "toast";

/** Rendering and commit timing belong to the host, never the network runtime. */
export interface RuntimeHost {
  transition(kind: RuntimeTransitionKind, commit: () => void): void;
}

export class Runtime {
  readonly store: StoreApi<UiState>;
  get state(): UiState {
    return this.store.getState();
  }
  private started = false;
  private disposed = false;
  private resumedLifetime: SessionLifetime | null = null;
  private unregisterUnauthorized: (() => void) | null = null;
  private channelGeneration = 0;
  private requestedChannel: number | null = null;
  private cardGeneration = 0;
  private readonly activityListener = () => this.noteActivity();
  private readonly visibilityListener = () => {
    if (document.visibilityState === "visible") this.noteActivity();
  };
  private socket: ChatSocket | null = null;
  readonly lang: Lang;
  readonly t: Dict;
  /** One worker drains this — sends must stay in composed order, because
   * reconcile adopts by nonce and patches in place. */
  private sendQueue: SendIntent[] = [];
  private sending = false;
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private toastTimers = new Set<ReturnType<typeof setTimeout>>();
  private lastActivityMs = Date.now();
  /** Callbacks for the host layer (sounds, notifications) — F3 wires them. */
  onRing: ((start: boolean) => void) | null = null;

  constructor(
    private readonly host: RuntimeHost = {
      transition: (_kind, commit) => commit(),
    },
  ) {
    this.store = createStore<UiState>(() => freeze(initialState(), true));
    this.lang = detectLang();
    this.t = dict(this.lang);
  }

  /** Bootstrap owns this registration; rerendering and route mounts are harmless. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.disposed = false;
    try {
      const tab = localStorage.getItem("echofloat.tab");
      if (tab === "all" || tab === "channels" || tab === "friends") {
        this.mutate((s) => {
          s.tab = tab;
        });
      }
    } catch {
      /* Storage can be unavailable; navigation still works in memory. */
    }
    this.unregisterUnauthorized =
      api.setUnauthorizedHandler(() => {
        if (!inReauthWindow()) void this.logout();
      }) ?? null;
    window.addEventListener("pointerdown", this.activityListener, {
      passive: true,
    });
    window.addEventListener("keydown", this.activityListener, { passive: true });
    document.addEventListener("visibilitychange", this.visibilityListener);
  }

  /** Technical teardown keeps the stored login. A subsequent resume reconnects. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    endLifetime();
    this.releaseResources();
    this.unregisterUnauthorized?.();
    this.unregisterUnauthorized = null;
    if (this.started) {
      window.removeEventListener("pointerdown", this.activityListener);
      window.removeEventListener("keydown", this.activityListener);
      document.removeEventListener("visibilitychange", this.visibilityListener);
      this.started = false;
    }
    this.mutate((s) => {
      s.connected = false;
      s.busy = false;
      s.voice = null;
      s.ringing = null;
      s.toasts = [];
    });
  }

  private transition(kind: RuntimeTransitionKind, commit: () => void): void {
    const lifetime = currentLifetime();
    this.host.transition(kind, () => {
      if (isCurrent(lifetime)) commit();
    });
  }

  /** UI commands report errors only to their originating account. Multi-step
   * commands use check() before issuing another request; apply never runs late. */
  private async sessionAction<T>(
    request: (check: () => void) => Promise<T>,
    apply?: (result: T) => void,
  ): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const result = await request(() => assertCurrent(lifetime));
      if (isCurrent(lifetime)) apply?.(result);
    } catch (error) {
      if (isCurrent(lifetime)) throw error;
    }
  }

  private mutate(fn: (draft: UiState) => void): void {
    this.store.setState(produce(this.state, fn), true);
  }

  private newSocket(): ChatSocket {
    const lifetime = currentLifetime();
    const socket = new ChatSocket((event) => {
      if (isCurrent(lifetime) && this.socket === socket) this.onSocketEvent(event);
    });
    return socket;
  }

  private releaseResources(): void {
    this.channelGeneration += 1;
    this.cardGeneration += 1;
    this.requestedChannel = null;
    this.resumedLifetime = null;
    this.socket?.dispose();
    this.socket = null;
    this.stopTick();
    for (const timer of this.toastTimers) clearTimeout(timer);
    this.toastTimers.clear();
    this.sendQueue = [];
    this.sending = false;
    this.voice?.leave();
    this.voice = null;
    this.sharePainterOwner = null;
    this.onRing?.(false);
  }

  /** Logout/account replacement invalidates I/O before any synchronous close callback. */
  private resetSession(): void {
    endLifetime();
    this.releaseResources();
    setSession(null);
    this.store.setState(freeze(initialState(), true), true);
  }

  // -------------------------------------------------------------------------
  // Login / logout

  async login(username: string, password: string): Promise<void> {
    this.start();
    this.resetSession();
    await this.authenticate(username, password);
  }

  private async authenticate(username: string, password: string): Promise<void> {
    const lifetime = currentLifetime();
    if ("Notification" in window && Notification.permission === "default") {
      void Notification.requestPermission();
    }
    this.mutate((s) => {
      s.busy = true;
      s.notice = null;
    });
    try {
      const { jwt } = await api.login(username, password);
      if (!isCurrent(lifetime)) return;
      setSession({ jwt, userId: 0, base: "" });
      this.mutate((s) => {
        s.selfUsername = username;
      });
      // Best-effort identity: a gateway too old to know /v1/me still logs in.
      try {
        const me = await api.fetchMe();
        if (!isCurrent(lifetime)) return;
        setSession({ jwt, userId: me.user_id, base: "" });
        this.adoptMe(me);
      } catch {
        if (!isCurrent(lifetime)) return;
        // The directory's username match becomes the fallback identity.
      }
      this.startSocket();
    } catch (error) {
      if (isCurrent(lifetime)) throw error;
    } finally {
      if (isCurrent(lifetime)) {
        this.mutate((s) => {
          s.busy = false;
        });
      }
    }
  }

  async register(username: string, email: string, password: string): Promise<void> {
    this.start();
    this.resetSession();
    const lifetime = currentLifetime();
    this.mutate((s) => {
      s.busy = true;
      s.notice = null;
    });
    try {
      await api.register(username, email, password);
      if (!isCurrent(lifetime)) return;
      await this.authenticate(username, password);
      if (!isCurrent(lifetime)) return;
    } catch (error) {
      if (isCurrent(lifetime)) throw error;
    } finally {
      if (isCurrent(lifetime)) {
        this.mutate((s) => {
          s.busy = false;
        });
      }
    }
  }

  /** Resume from a stored session (page load). Identity is re-adopted the
   * same best-effort way login does it — a reloaded page otherwise holds an
   * empty username, and the first profile save would send it. */
  resume(): boolean {
    this.start();
    const s = session();
    if (!s) return false;
    const lifetime = currentLifetime();
    if (this.resumedLifetime === lifetime) return true;
    this.resumedLifetime = lifetime;
    void api
      .fetchMe()
      .then((me) => {
        if (isCurrent(lifetime)) this.adoptMe(me);
      })
      .catch(() => {});
    this.startSocket();
    return true;
  }

  async logout(): Promise<void> {
    this.resetSession();
  }

  private adoptMe(me: import("../gen/dto").UserJson): void {
    this.mutate((s) => {
      s.selfUserId = me.user_id;
      s.selfUsername = me.username;
      s.directory.selfId = me.user_id;
      s.account.email = me.email;
      s.account.displayName = me.display_name;
      s.account.bio = me.bio;
      s.account.avatarFileId = me.avatar_file_id;
      s.account.createdAt = me.created_at;
      s.account.bubbleLight = me.bubble_light;
      s.account.bubbleDark = me.bubble_dark;
      s.account.manualStatus = normalizeManual(me.manual_status);
      s.account.isAdmin = me.is_admin;
      s.directory.people[me.user_id] = {
        username: me.username,
        displayName: me.display_name,
        bio: me.bio,
        avatarFileId: me.avatar_file_id,
        createdAt: me.created_at,
        bubbleLight: me.bubble_light,
        bubbleDark: me.bubble_dark,
      };
    });
  }

  private startSocket(): void {
    const s = session();
    if (!s) return;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    this.socket?.dispose();
    this.socket = this.newSocket();
    this.resumedLifetime = currentLifetime();
    this.socket.start(`${proto}://${location.host}/v1/chat/ws`, s.jwt);
    this.startTick();
  }

  // -------------------------------------------------------------------------
  // Socket events

  private onSocketEvent(e: SocketEvent): void {
    switch (e.t) {
      case "status":
        this.mutate((s) => {
          s.connected = e.connected;
        });
        return;
      case "ready": {
        this.mutate((s) => {
          // A fresh connection is "here" as far as the server is concerned.
          s.presence.selfAfk = false;
          if (mut.selfIdOf(s.directory) === 0) {
            s.directory.selfId = e.user_id;
            s.selfUserId = e.user_id;
          }
        });
        void this.sync();
        return;
      }
      case "auth_rejected": {
        // The socket is handed its JWT once and cannot refresh one — except
        // right after a password change, where the fresh JWT exists and this
        // rejection is the revocation doing its job (R44).
        if (inReauthWindow()) {
          const s = session();
          if (s) {
            this.startSocket();
            return;
          }
        }
        void this.logout();
        this.mutate((s) => {
          s.notice = e.message;
        });
        return;
      }
      case "event":
        this.onServerEvent(e.event);
        return;
    }
  }

  private onServerEvent(e: ServerEvent): void {
    switch (e.t) {
      case "message":
        this.onMessage(e as unknown as Message & { t: string });
        return;
      case "message_edited":
        this.onEdited(e as unknown as Message & { t: string });
        return;
      case "message_deleted":
        this.mutate((s) => {
          const open = s.feed.channelId === e.channel_id;
          if (open) mut.applyDeletion(s.feed, e.seq, this.t);
          // The sidebar quotes the newest message of every room, open or
          // not, and has to stop quoting one that is gone.
          const replacement = open ? (s.feed.bubbles.at(-1)?.message ?? null) : null;
          mut.roomsOnDeleted(s.rooms, e.channel_id, e.seq, replacement, this.t);
        });
        return;
      case "reaction":
        this.mutate((s) => {
          if (s.feed.channelId === e.channel_id) {
            mut.onReactionEvent(
              s.feed,
              e.seq,
              e.user_id,
              e.emoji,
              e.reactions,
              mut.selfIdOf(s.directory),
            );
          }
        });
        return;
      case "typing":
        this.mutate((s) => {
          if (s.feed.channelId === e.channel_id) {
            mut.onRemoteTyping(s.feed, e.user_id, mut.selfIdOf(s.directory), Date.now());
          }
        });
        return;
      case "read":
        // user_id is ignored: read marks only ever reach the reader's own
        // devices.
        this.applyReadMark(e.channel_id, e.last_read_seq);
        return;
      case "channel_created":
        this.mutate((s) => {
          mut.roomsOnCreated(
            s.rooms,
            e.channel_id,
            e.name,
            e.kind ?? "standard",
            e.peer_id ?? null,
            e.created_by,
            mut.selfIdOf(s.directory),
          );
        });
        return;
      case "channel_deleted":
        this.onChannelDeleted(e.channel_id);
        return;
      case "channel_renamed":
        this.mutate((s) => {
          mut.roomsOnUpdated(s.rooms, e);
          if (s.feed.channelId === e.channel_id) s.feed.channelName = e.name;
        });
        return;
      case "channel_settings":
        this.mutate((s) => {
          const row = mut.roomOf(s.rooms, e.channel_id);
          if (row) row.settings = e.settings;
        });
        return;
      case "membership":
        this.mutate((s) => {
          mut.roomsOnMembership(
            s.rooms,
            e.channel_id,
            e.user_id,
            e.change,
            e.admin,
            mut.selfIdOf(s.directory),
          );
        });
        return;
      case "profile_updated":
        this.mutate((s) => {
          mut.applyDirectoryUpdate(s.directory, {
            user_id: e.user_id,
            username: e.username,
            display_name: e.display_name,
            bio: e.bio,
            avatar_file_id: e.avatar_file_id,
            created_at: e.created_at,
            bubble_light: e.bubble_light,
            bubble_dark: e.bubble_dark,
          });
          if (mut.isSelf(s.directory, e.user_id)) {
            s.selfUsername = e.username;
            s.account.displayName = e.display_name;
            s.account.avatarFileId = e.avatar_file_id;
            s.account.bubbleLight = e.bubble_light;
            s.account.bubbleDark = e.bubble_dark;
          }
          this.resyncNames(s);
        });
        return;
      case "call_started":
        this.onCallStarted(e);
        return;
      case "call_participants":
        this.mutate((s) => {
          mut.callsApply(s.calls, e);
          const row = mut.roomOf(s.rooms, e.channel_id);
          if (row) mut.setRoomCall(row, e.participants, s.directory);
        });
        return;
      case "call_ended": {
        const stopRing = this.state.ringing?.channelId === e.channel_id;
        this.mutate((s) => {
          mut.callsEnd(s.calls, e.channel_id);
          const row = mut.roomOf(s.rooms, e.channel_id);
          if (row) mut.setRoomCall(row, [], s.directory);
          if (s.ringing?.channelId === e.channel_id) {
            s.ringing = null;
          }
        });
        if (stopRing) this.onRing?.(false);
        return;
      }
      case "call_declined": {
        const stopRing = this.state.ringing?.channelId === e.channel_id;
        this.mutate((s) => {
          // Our own other device declined: stop ringing here too.
          if (s.ringing?.channelId === e.channel_id) {
            s.ringing = null;
          }
        });
        if (stopRing) this.onRing?.(false);
        return;
      }
      case "friends":
        this.onFriends(e);
        return;
      case "presence":
        this.onPresence(e.user_id, e.status);
        return;
      case "presence_sync":
        this.mutate((s) => {
          mut.applyPresenceSync(s.presence, e.presence);
        });
        void this.recheckCard();
        return;
      case "my_presence":
        // Only the manual half is folded — `status` is derived from manual
        // plus THIS machine's idleness, and this machine is the authority.
        this.mutate((s) => {
          s.account.manualStatus = normalizeManual(e.manual);
        });
        return;
      case "error":
        this.mutate((s) => {
          s.notice = e.message;
        });
        return;
      default:
        return;
    }
  }

  private onMessage(m: Message): void {
    const own = mut.isSelf(this.state.directory, m.sender_id);
    this.mutate((s) => {
      mut.roomsOnMessage(s.rooms, m, s.selectedChannel, own, Date.now(), this.t);
      if (s.feed.channelId === m.channel_id) {
        mut.reconcile(s.feed, stripTag(m), this.t);
        if (own) s.feed.lastReadSeq = Math.max(s.feed.lastReadSeq, m.seq);
        mut.resolveNames(s.feed, s.directory, this.t);
      }
    });
    // A sender the directory has never heard of: schedule ONE refresh (the
    // 30 s TTL is the flood gate), the desktop's nameOfTracked shape.
    if (!this.state.directory.people[m.sender_id]) {
      void this.refreshDirectory(false);
    }
    // The notification gate: own → no; service → no; visible open
    // channel → no. A hidden tab notifies even for the open channel —
    // hidden is hidden.
    if (own || (m.service && m.service.kind !== undefined)) return;
    const visibleHere = !document.hidden && this.state.selectedChannel === m.channel_id;
    if (visibleHere) return;
    if ("Notification" in window && Notification.permission === "granted") {
      const room = this.state.rooms.find((r) => r.channelId === m.channel_id);
      const sender = mut.nameOf(this.state.directory, m.sender_id);
      const title = room && room.kind !== "dm" ? `${sender} — ${room.name}` : sender;
      const n = new Notification(title, {
        body: mut.preview(m, this.t),
        tag: `echofloat-${m.channel_id}`,
      });
      n.onclick = () => {
        window.focus();
        this.openChannel(m.channel_id);
        n.close();
      };
    }
  }

  private onEdited(m: Message): void {
    this.mutate((s) => {
      if (s.feed.channelId === m.channel_id) {
        mut.reconcile(s.feed, stripTag(m), this.t);
        // A call widget grows a participant on every edit; the roster is
        // resolved in resolveNames.
        mut.resolveNames(s.feed, s.directory, this.t);
      }
      mut.roomsOnEdited(s.rooms, m, this.t);
    });
  }

  private onChannelDeleted(channelId: number): void {
    if (this.requestedChannel === channelId) this.cancelChannelNavigation();
    const wasSelected = this.state.selectedChannel === channelId;
    this.mutate((s) => {
      const at = s.rooms.findIndex((r) => r.channelId === channelId);
      if (at !== -1) s.rooms.splice(at, 1);
      if (wasSelected) s.selectedChannel = null;
      mut.callsEnd(s.calls, channelId);
    });
    if (wasSelected) {
      const next = this.state.rooms[0]?.channelId;
      if (next !== undefined) {
        void this.selectChannel(next);
      } else {
        this.mutate((s) => {
          mut.openFeed(s.feed, 0, "", 0, 0);
          s.feed.loading = false;
        });
      }
    }
  }

  private onCallStarted(call: import("../gen/dto").Call): void {
    let startRing = false;
    this.mutate((s) => {
      mut.callsApply(s.calls, call);
      const row = mut.roomOf(s.rooms, call.channel_id);
      if (row) mut.setRoomCall(row, call.participants, s.directory);
      // Ring gate: not our own start (another device is normal), and only
      // for channels we are a member of.
      const own = mut.isSelf(s.directory, call.started_by);
      if (own || !row?.member) return;
      s.ringing = {
        channelId: call.channel_id,
        starterName: mut.nameOf(s.directory, call.started_by),
        channelName: row.name,
        sinceMs: Date.now(),
      };
      startRing = true;
    });
    if (startRing) this.onRing?.(true);
  }

  private onFriends(e: import("../gen/dto").FriendsSnapshot): void {
    let news: mut.FriendNews[] = [];
    this.mutate((s) => {
      news = mut.applyFriendsSnapshot(s.friends, e);
    });
    // The two quiet notifications (D174): derived by diffing, never claimed
    // by the frame; the request one opens the sender's card.
    for (const item of news) {
      const name = mut.nameOf(this.state.directory, item.userId);
      this.pushToast(
        item.t === "requested" ? this.t.toastRequest(name) : this.t.toastAccepted(name),
        item.t === "requested" ? item.userId : null,
      );
    }
    void this.recheckCard();
  }

  private toastSeq = 0;

  private pushToast(text: string, userId: number | null): void {
    const lifetime = currentLifetime();
    const id = ++this.toastSeq;
    this.transition("toast", () => {
      this.mutate((s) => {
        s.toasts.push({ id, text, userId });
      });
    });
    const timer = setTimeout(() => {
      this.toastTimers.delete(timer);
      if (!isCurrent(lifetime)) return;
      this.transition("toast", () => {
        this.mutate((s) => {
          const at = s.toasts.findIndex((x) => x.id === id);
          if (at !== -1) s.toasts.splice(at, 1);
        });
      });
    }, 6000);
    this.toastTimers.add(timer);
  }

  dismissToast(id: number): void {
    this.transition("toast", () => {
      this.mutate((s) => {
        const at = s.toasts.findIndex((x) => x.id === id);
        if (at !== -1) s.toasts.splice(at, 1);
      });
    });
  }

  private onPresence(userId: number, status: import("../gen/dto").PublicStatus): void {
    const before =
      this.state.card.userId !== null
        ? mut.statusOf(this.state.presence, this.state.card.userId) !== null
        : null;
    this.mutate((s) => {
      mut.applyPresenceFrame(s.presence, userId, status);
    });
    const after =
      this.state.card.userId !== null
        ? mut.statusOf(this.state.presence, this.state.card.userId) !== null
        : null;
    // Only the here↔gone crossing re-reads the relation (D192/D193).
    if (this.state.card.userId !== null && before !== after) void this.recheckCard();
  }

  private async recheckCard(): Promise<void> {
    const lifetime = currentLifetime();
    const userId = this.state.card.userId;
    const generation = this.cardGeneration;
    if (userId === null) return;
    try {
      const relation = await api.fetchFriendship(userId);
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        if (generation === this.cardGeneration && s.card.userId === userId)
          s.card.relation = relation;
      });
    } catch {
      // The card keeps its placeholder; a re-open retries.
    }
  }

  // -------------------------------------------------------------------------
  // The resync (chat-api.md §3.3 + D160)

  async sync(): Promise<void> {
    const lifetime = currentLifetime();
    if (!session()) return;
    await this.refreshDirectory(true);
    if (!isCurrent(lifetime)) return;
    let envelope: import("../gen/dto").StateResponse;
    try {
      envelope = await api.fetchState();
      if (!isCurrent(lifetime)) return;
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.fail(e);
      return;
    }
    this.mutate((s) => {
      s.notice = null;
      // Friends BEFORE rooms: a just-appeared DM row must already have a
      // friendship behind it when the Friends tab counts.
      mut.applyFriendsSnapshot(s.friends, envelope.friends);
      mut.applyPresenceState(s.presence, envelope.presence ?? null);
      mut.applyState(s.rooms, envelope.channels, s.directory, Date.now(), this.t);
      if (s.selectedChannel !== null && !mut.roomOf(s.rooms, s.selectedChannel)) {
        s.selectedChannel = null;
      }
      const open = s.selectedChannel;
      if (open !== null) {
        const row = mut.roomOf(s.rooms, open);
        if (row) {
          s.feed.maxSeq = row.maxSeq;
          s.feed.lastReadSeq = Math.max(s.feed.lastReadSeq, row.lastReadSeq);
        }
      }
    });

    // A URL coordinator may have requested an open synchronously from the
    // store notification above while its visual commit is still deferred.
    // Resync must not replace that navigation with the first/previous room.
    if (this.requestedChannel !== null) return;
    const open = this.state.selectedChannel;
    if (open !== null) {
      const newest = mut.newestSeq(this.state.feed) ?? 0;
      if (this.state.feed.maxSeq - newest > WINDOW_SIZE) {
        // The whole window is stale: reopening lands on the bottom in two
        // requests instead of dozens trimHead would throw away.
        await this.selectChannel(open, true);
        if (!isCurrent(lifetime)) return;
      } else {
        await this.loadNewer();
        if (!isCurrent(lifetime)) return;
        await this.refreshWindowReactions();
        if (!isCurrent(lifetime)) return;
      }
    } else {
      const first = this.state.rooms[0]?.channelId;
      if (first !== undefined) await this.selectChannel(first);
      if (!isCurrent(lifetime)) return;
    }
  }

  private async refreshDirectory(force: boolean): Promise<void> {
    const lifetime = currentLifetime();
    const dir = this.state.directory;
    const now = Date.now();
    if (!force && dir.lastRefreshMs !== null && now - dir.lastRefreshMs < 30_000) return;
    try {
      const entries = await api.fetchDirectory();
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        mut.applyDirectory(s.directory, entries, s.selfUsername);
        s.directory.lastRefreshMs = now;
        s.directory.refreshWanted = false;
        this.resyncNames(s);
      });
    } catch {
      // A directory that failed to refresh costs names, not data.
    }
  }

  /** The one function that rewrites every directory-fed cache. */
  private resyncNames(s: UiState): void {
    mut.resolveNames(s.feed, s.directory, this.t);
    for (const row of s.rooms) {
      if (row.callMemberIds.length > 0) {
        mut.setRoomCall(row, row.callMemberIds, s.directory);
      }
    }
  }

  private fail(e: unknown): void {
    if (e instanceof api.ApiError && e.unauthorized) {
      if (!inReauthWindow()) void this.logout();
      return;
    }
    this.mutate((s) => {
      s.notice = e instanceof Error ? e.message : String(e);
    });
  }

  // -------------------------------------------------------------------------
  // Channel lifecycle

  openChannel(id: number): void {
    void this.selectChannel(id);
  }

  /** Invalidate an abandoned route even if its transition has not committed yet. */
  cancelChannelNavigation(nextTarget?: number | null): void {
    // URL feedback for a runtime command acknowledges the same in-flight open;
    // invalidating it here would strand the loading feed without its response.
    if (nextTarget != null && nextTarget === this.requestedChannel) return;
    this.channelGeneration += 1;
    this.requestedChannel = null;
  }

  async selectChannel(id: number, force = false): Promise<void> {
    const lifetime = currentLifetime();
    if (!this.state.rooms.some((r) => r.channelId === id)) return;
    if (
      !force &&
      (this.requestedChannel === id ||
        (this.requestedChannel === null &&
          this.state.selectedChannel === id &&
          this.state.feed.channelId === id &&
          !this.state.feed.loading))
    )
      return;
    const generation = ++this.channelGeneration;
    this.requestedChannel = id;
    const current = () => isCurrent(lifetime) && generation === this.channelGeneration;
    // The host may defer its commit. Wait for it before reading the resulting
    // feed, and also check ownership *inside* it: A -> B -> A is three opens.
    await new Promise<void>((resolve) => {
      // A host may suppress its deferred callback when the account ends. The
      // caller must still settle instead of waiting forever for an old frame.
      const finish = () => {
        lifetime.signal.removeEventListener("abort", finish);
        resolve();
      };
      lifetime.signal.addEventListener("abort", finish, { once: true });
      this.host.transition("center", () => {
        try {
          if (current()) {
            this.mutate((s) => {
              const row = s.rooms.find((r) => r.channelId === id);
              if (!row) return;
              s.selectedChannel = id;
              mut.openFeed(s.feed, id, row.name, row.maxSeq, row.lastReadSeq);
            });
          }
        } finally {
          finish();
        }
      });
    });
    if (!current() || this.state.feed.channelId !== id) return;
    const { maxSeq, lastReadSeq } = this.state.feed;
    const unread = maxSeq > lastReadSeq && lastReadSeq > 0;
    try {
      const head = unread
        ? await api.fetchMessages(id, { before: lastReadSeq + 1, limit: 25 })
        : await api.fetchMessages(id, { limit: PAGE_SIZE });
      if (!current()) return;
      const tail = unread
        ? await api.fetchMessages(id, { after: lastReadSeq, limit: PAGE_SIZE })
        : { messages: [] as Message[] };
      if (!current()) return;
      const reachedStart = head.messages.length === 0 || (head.messages[0]?.seq ?? 0) <= 1;
      this.transition("center", () => {
        if (!current() || this.state.feed.channelId !== id) return;
        this.requestedChannel = null;
        this.mutate((s) => {
          s.feed.loading = false;
          splicePage(s.feed, head, true, this.t);
          splicePage(s.feed, tail as import("../gen/dto").PageResponse, true, this.t);
          s.feed.reachedStart = reachedStart;
          mut.insertUnreadDivider(s.feed);
          mut.resolveNames(s.feed, s.directory, this.t);
        });
      });
    } catch (e) {
      if (!current() || this.state.feed.channelId !== id) return;
      this.requestedChannel = null;
      this.mutate((s) => {
        s.feed.loading = false;
      });
      this.fail(e);
    }
  }

  async loadOlder(): Promise<void> {
    const lifetime = currentLifetime();
    const generation = this.channelGeneration;
    const feed = this.state.feed;
    if (feed.loadingOlder || feed.reachedStart || feed.bubbles.length === 0) return;
    const oldest = mut.oldestSeq(feed);
    if (oldest === undefined) return;
    const channel = feed.channelId;
    this.mutate((s) => {
      s.feed.loadingOlder = true;
    });
    try {
      const page = await api.fetchMessages(channel, {
        before: oldest,
        limit: PAGE_SIZE,
      });
      if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
      if (this.state.feed.channelId !== channel) return; // only the setter clears the flag
      this.mutate((s) => {
        s.feed.loadingOlder = false;
        if (page.messages.length === 0) {
          s.feed.reachedStart = true;
        } else {
          splicePage(s.feed, page, false, this.t);
          mut.trimTail(s.feed);
          mut.resolveNames(s.feed, s.directory, this.t);
        }
      });
    } catch (e) {
      if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
      if (this.state.feed.channelId !== channel) return;
      this.mutate((s) => {
        s.feed.loadingOlder = false;
        s.notice = e instanceof Error ? e.message : String(e);
      });
    }
  }

  /** The ?after= gap-fill — a loop, not one page: after a reconnect the
   * ceiling can be hundreds of seqs above the window. */
  private async loadNewer(): Promise<void> {
    const lifetime = currentLifetime();
    const generation = this.channelGeneration;
    for (;;) {
      const newest = mut.newestSeq(this.state.feed);
      if ((newest ?? 0) >= this.state.feed.maxSeq) return;
      const channel = this.state.feed.channelId;
      if (channel === 0) return;
      let page: import("../gen/dto").PageResponse;
      try {
        page = await api.fetchMessages(
          channel,
          newest === undefined ? { limit: PAGE_SIZE } : { after: newest, limit: PAGE_SIZE },
        );
        if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
      } catch (e) {
        if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
        this.fail(e);
        return;
      }
      const short = page.messages.length < PAGE_SIZE;
      if (this.state.feed.channelId !== channel) return;
      this.mutate((s) => {
        if (newest === undefined) {
          // That was the NEWEST page, not a continuation.
          s.feed.reachedStart = page.messages.length === 0 || (page.messages[0]?.seq ?? 0) <= 1;
        }
        splicePage(s.feed, page, true, this.t);
        mut.trimHead(s.feed);
        mut.resolveNames(s.feed, s.directory, this.t);
      });
      if (short) return;
    }
  }

  /** D160: `?after=` closed the message gap; the chips of what was already
   * on screen are the one thing it cannot heal. Best-effort. */
  private async refreshWindowReactions(): Promise<void> {
    const lifetime = currentLifetime();
    const generation = this.channelGeneration;
    const feed = this.state.feed;
    const oldest = mut.oldestSeq(feed);
    const ceiling = mut.newestSeq(feed);
    if (oldest === undefined || ceiling === undefined) return;
    let cursor = Math.max(0, oldest - 1);
    const channel = feed.channelId;
    while (cursor < ceiling) {
      let page: import("../gen/dto").PageResponse;
      try {
        page = await api.fetchMessages(channel, {
          after: cursor,
          limit: PAGE_SIZE * 2,
        });
        if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
      } catch {
        if (!isCurrent(lifetime) || generation !== this.channelGeneration) return;
        return; // a failure costs stale decoration, not data
      }
      const last = page.messages.at(-1)?.seq;
      if (last === undefined) return;
      const short = page.messages.length < PAGE_SIZE * 2;
      if (this.state.feed.channelId !== channel) return;
      this.mutate((s) => {
        splicePage(s.feed, page, true, this.t);
      });
      cursor = last;
      if (short) return;
    }
  }

  // -------------------------------------------------------------------------
  // Sending

  /** Every keystroke calls this; the throttle mutates regardless of channel
   * (the state lives on the feed, so switching channels resets it). */
  noteTyping(): void {
    let due = false;
    this.mutate((s) => {
      due = mut.shouldSendTyping(s.feed, Date.now());
    });
    const channel = this.state.feed.channelId;
    if (due && channel !== 0) this.socket?.sendTyping(channel);
  }

  send(text: string, staged: StagedFile[] = []): void {
    if (text.trim() === "" && staged.length === 0) return;
    this.prepareSend()?.(text, staged);
  }

  /** Snapshot the user's destination before any async attachment preparation. */
  prepareSend(): PreparedSend | null {
    const s = this.state;
    const channel = s.feed.channelId;
    if (channel === 0 || !session()) return null;
    const row = s.rooms.find((r) => r.channelId === channel);
    const accountId = mut.selfIdOf(s.directory);
    const composerRevision = s.feed.composerRevision;
    const mode = s.feed.mode;
    const context: SendContext = Object.freeze({
      lifetime: currentLifetime(),
      accountId,
      selfName: mut.nameOf(s.directory, accountId),
      channel,
      replyTo: s.feed.mode.t === "replying" ? s.feed.mode.seq : null,
      editing: s.feed.mode.t === "editing" ? s.feed.mode.seq : null,
      nonce: crypto.randomUUID(),
      maxChars: row?.settings.max_message_chars ?? 4096,
      unlimited: s.account.isAdmin,
      pause: row?.admin ? 0 : (row?.settings.slowmode_s ?? 0),
    });
    // Capture now, consume only once a real message is queued. A discarded
    // recording keeps the reply/edit; a newer composition is never cleared.
    let consumed = false;
    return (text, staged = []) => {
      if (consumed || !isCurrent(context.lifetime)) return;
      if (!this.enqueueSend(context, text, staged)) return;
      consumed = true;
      if (
        this.state.feed.channelId === channel &&
        this.state.feed.composerRevision === composerRevision &&
        this.state.feed.mode === mode
      ) {
        this.cancelMode();
      }
    };
  }

  private enqueueSend(context: SendContext, text: string, staged: StagedFile[]): boolean {
    const parsed = parseMarkdown(text);
    if (parsed.text.trim() === "" && staged.length === 0) return false;
    const limit = Math.min(staged.length > 0 ? 1024 : 4096, context.maxChars);
    const split =
      context.editing === null
        ? splitMessage(parsed.text, parsed.entities, limit)
        : { parts: [parsed], truncated: false };
    if (split.parts.length === 0) return false;
    if (split.truncated) this.showNotice(this.t.messageTooLongSplit(MAX_SPLIT_PARTS));
    const intent: SendIntent = Object.freeze({
      ...context,
      parts: Object.freeze(
        split.parts.map((part, index) =>
          Object.freeze({
            ...part,
            nonce: index === 0 ? context.nonce : crypto.randomUUID(),
          }),
        ),
      ),
      staged: Object.freeze(staged.map((item) => Object.freeze({ ...item }))),
    });
    this.sendQueue.push(intent);
    void this.drainSends();
    return true;
  }

  private async drainSends(): Promise<void> {
    const lifetime = currentLifetime();
    if (this.sending) return;
    this.sending = true;
    try {
      for (;;) {
        const intent = this.sendQueue.shift();
        if (!intent) return;
        if (!isCurrent(intent.lifetime)) continue;
        await this.sendNow(intent);
        if (!isCurrent(lifetime)) return;
      }
    } catch (error) {
      if (isCurrent(lifetime)) this.fail(error);
    } finally {
      if (isCurrent(lifetime)) {
        this.sending = false;
      }
    }
  }

  private async sendNow(intent: SendIntent): Promise<void> {
    const lifetime = currentLifetime();
    if (!isCurrent(intent.lifetime)) return;
    const { channel, editing, replyTo, staged, accountId: selfId, selfName } = intent;
    const [first, ...tail] = intent.parts;
    if (editing !== null) {
      await this.commitEdit(channel, editing, first!.text, first!.entities);
      if (!isCurrent(lifetime)) return;
      return;
    }
    const nonce = first!.nonce;
    const optimistic: Message = {
      channel_id: channel,
      seq: PENDING_SEQ,
      sender_id: selfId,
      sent_at_ms: Date.now(),
      nonce,
      reply_to_seq: replyTo,
      text: first!.text,
      entities: first!.entities,
      attachments: [],
    } as unknown as Message;

    this.mutate((st) => {
      if (st.feed.channelId !== channel) return;
      const b = mut.newBubble(optimistic, selfName, true);
      b.state = staged.length > 0 ? "preparing" : "sending";
      b.localPreviews = staged.map((f) => f.previewUrl).filter((u): u is string => !!u);
      st.feed.bubbles.push(b); // PENDING_SEQ sorts it last by construction
      mut.regroup(st.feed);
      mut.fillReplyPreviews(st.feed, this.t);
    });

    // Attachments first (chat-api.md §2.6): sha-dedup, 1 MiB chunks, per-file
    // progress folded onto the bubble by nonce.
    const refs: import("../gen/dto").AttachmentInput[] = [];
    for (let i = 0; i < staged.length; i += 1) {
      const item = staged[i]!;
      const onProgress = (fraction: number) => {
        if (!isCurrent(intent.lifetime)) return;
        this.mutate((st) => {
          const b = st.feed.bubbles.find((x) => x.message.nonce === nonce);
          if (b) b.sendProgress = (i + fraction) / staged.length;
        });
      };
      try {
        const sendKind = sendKindOf(item);
        // A picture goes as two weights (D233): the 15 KiB thumbnail first,
        // so the far side has something to draw a second after «send», then
        // the 300 KiB preview that becomes the attachment itself.
        if (sendKind === "photo" && photoWeightsSupported()) {
          const { preview, thumb } = await photoWeights(item.file);
          if (!isCurrent(lifetime)) return;
          const thumbUp = await upload(
            thumb.blob,
            "photo",
            { width: thumb.width, height: thumb.height },
            (f) => onProgress(f * THUMB_SHARE),
          );
          if (!isCurrent(lifetime)) return;
          const previewUp = await upload(
            preview.blob,
            "photo",
            {
              fileName: item.file.name,
              width: preview.width,
              height: preview.height,
            },
            (f) => onProgress(THUMB_SHARE + f * (1 - THUMB_SHARE)),
          );
          if (!isCurrent(lifetime)) return;
          refs.push({
            file_id: previewUp.file_id,
            thumb_file_id: thumbUp.file_id,
            ...(item.file.name !== "" ? { file_name: item.file.name } : {}),
            ...(item.spoiler ? { spoiler: true } : {}),
          });
          continue;
        }
        // A picture that took the file door is still a picture: the budget
        // that applies is the original's, not the 100 MiB an installer gets.
        if (item.kind === "photo" && item.file.size > ORIGINAL_MAX_BYTES) {
          throw new UploadError(0, this.t.originalOverLimit(ORIGINAL_MAX_BYTES));
        }
        const probed = await probeFile(item, lifetime);
        if (!isCurrent(lifetime)) return;
        const result = await upload(
          item.file,
          sendKind,
          {
            fileName: item.file.name,
            ...(probed.width !== undefined ? { width: probed.width } : {}),
            ...(probed.height !== undefined ? { height: probed.height } : {}),
            ...(probed.durationMs !== undefined ? { durationMs: probed.durationMs } : {}),
          },
          onProgress,
          { unlimited: sendKind === "file" && intent.unlimited },
        );
        if (!isCurrent(lifetime)) return;
        refs.push({
          file_id: result.file_id,
          ...(item.file.name !== "" ? { file_name: item.file.name } : {}),
          ...(item.waveform !== undefined ? { waveform: item.waveform } : {}),
          ...(item.spoiler ? { spoiler: true } : {}),
        });
      } catch (e) {
        if (!isCurrent(lifetime)) return;
        this.strandTail(channel, tail);
        this.markFailed(nonce, e instanceof Error ? e.message : String(e));
        return;
      }
    }

    this.mutate((st) => {
      const b = st.feed.bubbles.find((x) => x.message.nonce === nonce);
      if (b) b.state = "sending";
    });

    const body: SendBody = {
      nonce,
      text: first!.text,
      entities: first!.entities,
      attachments: refs,
      ...(replyTo !== null ? { reply_to_seq: replyTo } : {}),
    };

    try {
      const server = await api.sendMessage(channel, body);
      if (!isCurrent(lifetime)) return;
      if (this.state.feed.channelId === channel) {
        this.mutate((st) => {
          mut.reconcile(st.feed, server, this.t);
          mut.resolveNames(st.feed, st.directory, this.t);
        });
      }
      await this.markReadAt(channel, server.seq);
      if (!isCurrent(lifetime)) return;
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.strandTail(channel, tail);
      this.markFailed(nonce, e instanceof Error ? e.message : String(e));
      return;
    }

    // The tail, sequentially: seq is what the feed sorts on. Slow mode is
    // not bypassed for admins server-side exemption — the row knows.
    const pause = intent.pause;
    for (const part of tail) {
      if (pause > 0) await sessionDelay(pause * 1000, intent.lifetime);
      if (!isCurrent(lifetime)) return;
      const ok = await this.sendPart(intent, part);
      if (!isCurrent(lifetime)) return;
      if (!ok) {
        const rest = tail.slice(tail.indexOf(part) + 1);
        this.strandTail(channel, rest);
        return;
      }
    }
  }

  private async sendPart(intent: SendIntent, part: SendIntent["parts"][number]): Promise<boolean> {
    const lifetime = currentLifetime();
    if (!isCurrent(intent.lifetime)) return false;
    const { channel, accountId: selfId, selfName } = intent;
    const { text, entities, nonce } = part;
    const optimistic: Message = {
      channel_id: channel,
      seq: PENDING_SEQ,
      sender_id: selfId,
      sent_at_ms: Date.now(),
      nonce,
      text,
      entities,
      attachments: [],
    } as unknown as Message;
    if (this.state.feed.channelId === channel) {
      this.mutate((st) => {
        const b = mut.newBubble(optimistic, selfName, true);
        b.state = "sending";
        st.feed.bubbles.push(b);
        mut.regroup(st.feed);
      });
    }
    try {
      const server = await api.sendMessage(channel, {
        nonce,
        text,
        entities,
        attachments: [],
      });
      if (!isCurrent(lifetime)) return false;
      if (this.state.feed.channelId === channel) {
        this.mutate((st) => {
          mut.reconcile(st.feed, server, this.t);
          mut.resolveNames(st.feed, st.directory, this.t);
        });
      }
      await this.markReadAt(channel, server.seq);
      if (!isCurrent(lifetime)) return false;
      return true;
    } catch (e) {
      if (!isCurrent(lifetime)) return false;
      this.markFailed(nonce, e instanceof Error ? e.message : String(e));
      return false;
    }
  }

  /** A failed bubble stays in the feed with its text intact — the composer
   * already cleared itself, so the feed is the only place the text survives.
   * There is no automatic retry. */
  private markFailed(nonce: string, message: string): void {
    this.mutate((s) => {
      const b = s.feed.bubbles.find((x) => x.message.nonce === nonce);
      if (b) b.state = "failed";
      s.notice = message;
    });
  }

  /** Never post past a hole: unsent parts become visible failed bubbles. */
  private strandTail(
    channel: number,
    parts: Array<{ text: string; entities: import("../gen/dto").Entity[] }>,
  ): void {
    if (parts.length === 0) return;
    if (this.state.feed.channelId !== channel) return; // never paint another channel
    const selfId = mut.selfIdOf(this.state.directory);
    const selfName = mut.nameOf(this.state.directory, selfId);
    this.mutate((s) => {
      for (const part of parts) {
        const m: Message = {
          channel_id: channel,
          seq: PENDING_SEQ,
          sender_id: selfId,
          sent_at_ms: Date.now(),
          nonce: crypto.randomUUID(),
          text: part.text,
          entities: part.entities,
          attachments: [],
        } as unknown as Message;
        const b = mut.newBubble(m, selfName, true);
        b.state = "failed";
        s.feed.bubbles.push(b);
      }
      mut.regroup(s.feed);
    });
  }

  private async commitEdit(
    channel: number,
    seq: number,
    text: string,
    entities: import("../gen/dto").Entity[],
  ): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const server = await api.editMessage(channel, seq, { text, entities });
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        if (s.feed.channelId !== channel) return;
        const b = s.feed.bubbles.find((x) => x.message.seq === seq);
        if (b) b.message = server;
        // Every reply quoting this message still carries the pre-edit text.
        mut.fillReplyPreviews(s.feed, this.t);
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.fail(e);
    }
  }

  beginReply(seq: number): void {
    this.mutate((s) => {
      const b = s.feed.bubbles.find((x) => x.message.seq === seq);
      if (!b) return;
      s.feed.mode = { t: "replying", seq };
      s.feed.composerRevision += 1;
    });
  }

  beginEdit(seq: number): void {
    this.mutate((s) => {
      const b = s.feed.bubbles.find((x) => x.message.seq === seq);
      if (!b || !b.isOwn) return;
      s.feed.mode = { t: "editing", seq };
      s.feed.composerRevision += 1;
    });
  }

  cancelMode(): void {
    this.mutate((s) => {
      s.feed.mode = { t: "new" };
      s.feed.composerRevision += 1;
    });
  }

  async deleteMessage(seq: number): Promise<void> {
    const lifetime = currentLifetime();
    const channel = this.state.feed.channelId;
    if (channel === 0) return;
    try {
      await api.deleteMessage(channel, seq);
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        const open = s.feed.channelId === channel;
        if (open) mut.applyDeletion(s.feed, seq, this.t);
        const replacement = open ? (s.feed.bubbles.at(-1)?.message ?? null) : null;
        mut.roomsOnDeleted(s.rooms, channel, seq, replacement, this.t);
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.fail(e);
    }
  }

  // -------------------------------------------------------------------------
  // Reactions (D145–D167)

  async toggleReaction(seq: number, emoji: string): Promise<void> {
    const lifetime = currentLifetime();
    const s = this.state;
    const channel = s.feed.channelId;
    if (channel === 0) return;
    const b = s.feed.bubbles.find((x) => x.message.seq === seq);
    if (!b) return;
    const selfId = mut.selfIdOf(s.directory);
    const next = b.myReaction === emoji ? null : emoji; // picking the set emoji clears it
    let previous: string | null = null;
    this.mutate((st) => {
      previous = mut.setMyReaction(st.feed, seq, next, selfId);
    });
    try {
      const answer = next
        ? await api.setReaction(channel, seq, next)
        : await api.clearReaction(channel, seq);
      if (!isCurrent(lifetime)) return;
      if (this.state.feed.channelId === channel) {
        this.mutate((st) => {
          mut.applyReactions(st.feed, seq, answer.reactions);
          const bb = st.feed.bubbles.find((x) => x.message.seq === seq);
          if (bb) bb.myReaction = answer.my_reaction ?? null;
        });
      }
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      if (this.state.feed.channelId === channel) {
        this.mutate((st) => {
          mut.setMyReaction(st.feed, seq, previous, selfId);
        });
      }
      this.mutate((st) => {
        st.notice = e instanceof Error ? e.message : String(e);
      });
    }
  }

  // -------------------------------------------------------------------------
  // Read marks

  /** The reader reached the bottom (scroll spam calls this; the divider
   * check keeps the repaints away). */
  markRead(): void {
    if (!this.canRead()) return;
    if (this.state.feed.dividerBefore !== null) {
      this.mutate((s) => {
        mut.dismissUnreadDivider(s.feed);
      });
    }
    const seq = mut.readableSeq(this.state.feed);
    if (seq === null) return;
    void this.markReadAt(this.state.feed.channelId, seq);
  }

  /** A message has been wholly on screen while the tab was in front (D249):
   * the badge comes down as messages pass under the eye instead of waiting for
   * the bottom. The unread divider stays — reaching the end is what takes that
   * down, and that goes through `markRead` above. */
  markReadUpTo(seen: number): void {
    if (!this.canRead()) return;
    const seq = mut.readableSeqUpTo(this.state.feed, seen);
    if (seq === null) return;
    void this.markReadAt(this.state.feed.channelId, seq);
  }

  private canRead(): boolean {
    return !document.hidden && (typeof document.hasFocus !== "function" || document.hasFocus());
  }

  private async markReadAt(channel: number, seq: number): Promise<void> {
    const lifetime = currentLifetime();
    if (seq === PENDING_SEQ) return; // must never be posted (poisons the mark)
    try {
      const { last_read_seq } = await api.markRead(channel, seq);
      if (!isCurrent(lifetime)) return;
      this.applyReadMark(channel, last_read_seq);
    } catch {
      if (!isCurrent(lifetime)) return;
      // A lost read mark costs a badge, not data.
    }
  }

  private applyReadMark(channel: number, effective: number): void {
    this.mutate((s) => {
      mut.roomsOnRead(s.rooms, channel, effective);
      if (s.feed.channelId === channel && s.feed.lastReadSeq < effective) {
        s.feed.lastReadSeq = effective; // never backwards
      }
    });
  }

  // -------------------------------------------------------------------------
  // Join / membership (D114/D116)

  async join(channelId: number, password?: string): Promise<void> {
    const lifetime = currentLifetime();
    this.mutate((s) => {
      const row = mut.roomOf(s.rooms, channelId);
      if (row) row.joining = true;
    });
    try {
      const joined = await api.joinChannel(channelId, password ? { password } : {});
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        mut.roomsOnJoined(s.rooms, channelId, joined.admin);
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      const needsPassword = e instanceof api.ApiError && e.status === 409;
      this.mutate((s) => {
        const row = mut.roomOf(s.rooms, channelId);
        if (row) {
          row.joining = false;
          row.joinPasswordNeeded = needsPassword;
        }
        // A 409 is not an error to report — it is the channel asking for its
        // word; the join bar swaps its button for a field.
        if (!needsPassword) s.notice = e instanceof Error ? e.message : String(e);
      });
    }
  }

  async joinByToken(channelId: number, token: string): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const joined = await api.joinChannel(channelId, { token });
      if (!isCurrent(lifetime)) return;
      this.mutate((s) => {
        mut.roomsOnJoined(s.rooms, channelId, joined.admin);
      });
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.fail(e);
    }
  }

  async createChannel(name: string): Promise<void> {
    const lifetime = currentLifetime();
    try {
      const { channel_id } = await api.createChannel(name);
      if (!isCurrent(lifetime)) return;
      await this.sync();
      if (!isCurrent(lifetime)) return;
      this.openChannel(channel_id);
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      this.fail(e);
    }
  }

  // -------------------------------------------------------------------------
  // The 2 s tick: typing expiry, ring expiry, idle transitions — one clock.

  private startTick(): void {
    this.stopTick();
    this.tickTimer = setInterval(() => this.tick(), TICK_MS);
  }

  private stopTick(): void {
    if (this.tickTimer !== null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  private noteActivity(): void {
    this.lastActivityMs = Date.now();
  }

  private tick(): void {
    const now = Date.now();
    const stopRing = this.state.ringing !== null && now - this.state.ringing.sinceMs >= RING_FOR_MS;
    this.mutate((s) => {
      mut.tickTyping(s.feed, now);
      if (stopRing) s.ringing = null;
    });
    if (stopRing) this.onRing?.(false);
    // Presence transitions (D205): in-page signals only, 300 s threshold,
    // transitions-only behind the double gate.
    const away = now - this.lastActivityMs >= AFK_AFTER_MS;
    const s = this.state;
    const speak = s.connected && s.presence.enabled && s.presence.selfAfk !== away;
    if (speak) {
      this.mutate((st) => {
        st.presence.selfAfk = away;
      });
      this.socket?.sendActivity(away);
    }
  }

  // -------------------------------------------------------------------------

  setTab(tab: import("./types").SidebarTab): void {
    this.transition("tab", () => {
      this.mutate((s) => {
        s.tab = tab;
      });
    });
    try {
      localStorage.setItem("echofloat.tab", tab);
    } catch {
      /* Keep the live selection. */
    }
  }

  clearNotice(): void {
    this.transition("toast", () => {
      this.mutate((s) => {
        s.notice = null;
      });
    });
  }

  showNotice(text: string): void {
    this.transition("toast", () => {
      this.mutate((s) => {
        s.notice = text;
      });
    });
  }

  // ---------------------------------------------------------------------
  // The own profile (F2)

  /** `""` clears, and every answer is the whole UserJson (D64/D65). */
  saveProfile(displayName: string, bio: string): Promise<void> {
    return this.sessionAction(
      () =>
        api.updateProfile({
          username: this.state.selfUsername,
          display_name: displayName.trim(),
          bio: bio.trim(),
        }),
      (me) => this.adoptMe(me),
    );
  }

  uploadAvatar(file: File): Promise<void> {
    return this.sessionAction(
      async (check) => {
        const result = await upload(file, "photo", { fileName: file.name }, () => {}, {});
        check();
        return api.setAvatar(result.file_id);
      },
      (me) => this.adoptMe(me),
    );
  }

  removeAvatar(): Promise<void> {
    return this.sessionAction(
      () => api.deleteAvatar(),
      (me) => this.adoptMe(me),
    );
  }

  /** R44: the change revokes older tokens, so the socket's next rejection is
   * expected — the window is armed BEFORE the request, the fresh JWT is
   * adopted from the answer, and the socket redials with it instead of
   * logging out. */
  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    armReauthWindow();
    return this.sessionAction(
      () => api.changePassword(currentPassword, newPassword),
      ({ jwt }) => {
        const s = session();
        if (s) setSession({ ...s, jwt });
        this.startSocket(); // redial with the fresh JWT
      },
    );
  }

  // ---------------------------------------------------------------------
  // The user card + friends (F3)

  /** Always a FRESH friendship read (F3): the card shows a placeholder
   * while it flies, never defaults. */
  openCard(userId: number): void {
    const generation = ++this.cardGeneration;
    // The recheck stays INSIDE the swap callback: a view transition runs its
    // callback at capture time (next frame), and recheckCard reads
    // state.card.userId — called outside, it would see the card still closed.
    this.transition("sheet", () => {
      if (generation !== this.cardGeneration) return;
      this.mutate((s) => {
        s.card = { userId, relation: null };
      });
      void this.recheckCard();
    });
  }

  closeCard(): void {
    const generation = ++this.cardGeneration;
    this.transition("sheet", () => {
      if (generation !== this.cardGeneration) return;
      this.mutate((s) => {
        s.card = { userId: null, relation: null };
      });
    });
  }

  /** Re-read after an action moved the relation. */
  reopenCard(): Promise<void> {
    return this.recheckCard();
  }

  addFriend(userId: number): Promise<void> {
    return this.sessionAction(() => api.sendFriendRequest(userId));
  }

  cancelRequest(userId: number): Promise<void> {
    return this.sessionAction(() => api.cancelFriendRequest(userId));
  }

  acceptRequest(userId: number): Promise<void> {
    return this.sessionAction(() => api.acceptFriendRequest(userId));
  }

  /** Silent by design (D171): nothing reaches the sender. */
  declineRequest(userId: number): Promise<void> {
    return this.sessionAction(() => api.declineFriendRequest(userId));
  }

  unfriendUser(userId: number): Promise<void> {
    return this.sessionAction(() => api.unfriend(userId));
  }

  blockUser(userId: number): Promise<void> {
    return this.sessionAction(() => api.blockUser(userId));
  }

  unblock(userId: number): Promise<void> {
    return this.sessionAction(() => api.unblockUser(userId));
  }

  /** `auto | dnd | invisible` (D183); the answer is the whole UserJson. */
  setManualStatus(status: "auto" | "dnd" | "invisible"): Promise<void> {
    return this.sessionAction(
      () => api.setStatus(status),
      (me) => this.adoptMe(me),
    );
  }

  /** Decline a DM call (D178): per-callee and silent-to-others. */
  async declineIncomingCall(): Promise<void> {
    const lifetime = currentLifetime();
    const ring = this.state.ringing;
    if (!ring) return;
    this.transition("toast", () => {
      this.mutate((s) => {
        s.ringing = null;
      });
    });
    this.onRing?.(false);
    const row = this.state.rooms.find((r) => r.channelId === ring.channelId);
    if (row && row.kind === "dm") {
      await api.declineCall(ring.channelId).catch(() => {});
      if (!isCurrent(lifetime)) return;
    }
  }

  // -------------------------------------------------------------------------
  // Voice (F4)

  private voice: VoiceSession | null = null;

  async joinVoice(channelId: number): Promise<void> {
    const lifetime = currentLifetime();
    if (!voiceSupported()) {
      this.showNotice(this.t.voiceUnsupported());
      return;
    }
    if (this.state.voice?.channelId === channelId) return;
    if (this.voice) this.leaveVoice();
    const sess = session();
    if (!sess) return;
    // Answering by joining IS the answer — the ring card goes away.
    if (this.state.ringing?.channelId === channelId) {
      this.transition("toast", () => {
        this.mutate((s) => {
          s.ringing = null;
        });
      });
      this.onRing?.(false);
    }
    this.mutate((s) => {
      s.voice = mut.voiceConnecting(channelId);
    });
    const voice = new VoiceSession({
      jwt: sess.jwt,
      events: this.voiceEvents(channelId, () => this.voice === voice),
    });
    this.voice = voice;
    try {
      await voice.join(channelId, false);
      if (!isCurrent(lifetime)) return;
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      if (this.voice === voice) {
        this.voice = null;
        this.mutate((s) => {
          s.voice = null;
        });
        const text = e instanceof Error ? e.message : String(e);
        this.showNotice(
          /getUserMedia|Permission|NotAllowed/i.test(text) ? this.t.voiceMicDenied() : text,
        );
        voice.leave();
      }
    }
  }

  leaveVoice(): void {
    if (this.voice) sounds.play("leave");
    this.voice?.leave();
    this.voice = null;
    this.mutate((s) => {
      s.voice = null;
    });
  }

  toggleMute(): void {
    const v = this.state.voice;
    if (!v || !this.voice) return;
    const next = !v.muted;
    sounds.play(next ? "mute" : "unmute");
    this.voice.setMuted(next);
    this.mutate((s) => {
      if (s.voice) s.voice.muted = next;
    });
  }

  toggleDeafen(): void {
    const v = this.state.voice;
    if (!v || !this.voice) return;
    const next = !v.deafened;
    this.voice.setDeafened(next);
    this.mutate((s) => {
      if (s.voice) s.voice.deafened = next;
    });
  }

  setVoiceVolume(userId: number, volume: number): void {
    this.voice?.setVolume(userId, volume);
    this.mutate((s) => {
      if (s.voice) s.voice.volumes[userId] = volume;
    });
  }

  // -------------------------------------------------------------------------
  // Stream viewing (F5)

  watchStream(userId: number): void {
    const v = this.state.voice;
    const peer = v?.peers.find((p) => p.userId === userId);
    if (!v || !peer || !peer.screenSharing || !this.voice) return;
    if (!shareViewSupported()) {
      this.showNotice(this.t.voiceUnsupported());
      return;
    }
    this.voice.watch({
      senderId: peer.senderId,
      hasAudio: peer.screenShareHasAudio,
    });
    this.voice.setShareVolume(1);
    this.mutate((s) => {
      if (s.voice) {
        s.voice.share = {
          userId,
          paused: peer.screenSharePaused,
          hasAudio: peer.screenShareHasAudio,
          volume: 1,
        };
      }
    });
  }

  stopWatching(): void {
    this.voice?.unwatch();
    this.mutate((s) => {
      if (s.voice) s.voice.share = null;
    });
  }

  setShareVolume(volume: number): void {
    this.voice?.setShareVolume(volume);
    this.mutate((s) => {
      if (s.voice?.share) s.voice.share.volume = volume;
    });
  }

  /** The stream view's canvas registers its painter for decoded frames;
   * null unregisters. Frames are VideoFrames the painter must close. */
  private sharePainterOwner: object | null = null;

  registerSharePainter(
    painter: ((senderId: number, frame: VideoFrame) => void) | null,
  ): () => void {
    const owner = {};
    const voice = this.voice;
    this.sharePainterOwner = owner;
    voice?.setPainter(painter);
    return () => {
      if (this.sharePainterOwner !== owner) return;
      this.sharePainterOwner = null;
      if (this.voice === voice) voice?.setPainter(null);
    };
  }

  private shareEndText(reason: number): string {
    switch (reason) {
      case 2:
        return this.t.streamEndedSourceGone();
      case 3:
        return this.t.streamEndedLeft();
      case 4:
        return this.t.streamEndedAdmin();
      default:
        return this.t.streamEndedStopped();
    }
  }

  /** Every VoiceSession event lands in the store through here; the guard
   * keeps a late event from a torn-down session off the next call's state. */
  private voiceEvents(channelId: number, ownsCall: () => boolean): VoiceEvents {
    const lifetime = currentLifetime();
    const mutateVoice = (fn: (v: import("./types").VoiceCall) => void) => {
      this.mutate((s) => {
        if (s.voice && s.voice.channelId === channelId) fn(s.voice);
      });
    };
    const events: VoiceEvents = {
      onUp: () => {
        sounds.play("join");
        mutateVoice((v) => {
          v.phase = "up";
        });
      },
      onRoster: (peers) =>
        mutateVoice((v) => {
          v.peers = peers.map((p) => ({ ...p }));
        }),
      onPeerJoined: (peer) => {
        sounds.play("join");
        mutateVoice((v) => mut.voicePeerUpsert(v, { ...peer }));
      },
      onPeerLeft: (userId) => {
        sounds.play("leave");
        mutateVoice((v) => mut.voicePeerLeft(v, userId));
      },
      onPeerVoice: (userId, muted, deafened) =>
        mutateVoice((v) => mut.voicePeerFlags(v, userId, muted, deafened)),
      onPeerRtt: (userId, rttMs) => mutateVoice((v) => mut.voicePeerRtt(v, userId, rttMs)),
      onServerMute: (userId, muted) => mutateVoice((v) => mut.voiceServerMute(v, userId, muted)),
      onSpeakers: (userIds) =>
        mutateVoice((v) => {
          v.speaking = userIds;
        }),
      onPeerShare: (userId, share) => {
        const wasWatching = this.state.voice?.share?.userId === userId;
        mutateVoice((v) =>
          mut.voicePeerShare(v, userId, share.active, share.paused, share.hasAudio),
        );
        if (wasWatching && !share.active) {
          this.voice?.unwatch();
          this.showNotice(this.shareEndText(share.reason));
        }
      },
      onCode: (code) =>
        mutateVoice((v) => {
          v.code = code;
        }),
      onStats: (stats) =>
        mutateVoice((v) => {
          v.rttMs = stats.rttMs;
          v.lossPct = stats.lossPct;
          v.concealmentPct = stats.concealmentPct;
        }),
      onEnded: (reason) => {
        if (this.state.voice?.channelId !== channelId) return;
        this.voice = null;
        this.mutate((s) => {
          s.voice = null;
        });
        if (reason === "dropped" || reason === "serverError") {
          this.showNotice(this.t.voiceDropped());
        }
      },
    };
    return Object.fromEntries(
      Object.entries(events).map(([name, callback]) => [
        name,
        (...args: unknown[]) => {
          if (isCurrent(lifetime) && ownsCall()) Reflect.apply(callback, events, args);
        },
      ]),
    ) as unknown as VoiceEvents;
  }
}

function splicePage(
  feed: import("./types").FeedState,
  page: import("../gen/dto").PageResponse,
  atEnd: boolean,
  t: Dict,
): void {
  const first = page.messages[0]?.seq;
  const last = page.messages.at(-1)?.seq;
  const messages = page.messages.map(sanitizeMessage);
  for (const message of messages) {
    // Refreshes also restore edits missed during disconnection, and a history
    // page may itself be the first acknowledgement of a pending send.
    if (
      feed.bubbles.some(
        (b) =>
          b.message.seq === message.seq ||
          (message.nonce !== "" && b.message.nonce === message.nonce && b.state !== "sent"),
      )
    ) {
      mut.reconcile(feed, message, t);
    }
  }
  mut.appendMessages(feed, messages, atEnd);
  // The span half matters even when append inserts nothing — a visible-range
  // re-fetch after a socket drop is all duplicates, and the chips are the
  // part that still has to land (D160).
  if (first !== undefined && last !== undefined) {
    mut.applyPageReactions(feed, [first, last], page.reactions ?? [], page.my_reactions ?? []);
  }
}

/** The lossy-page rule (chat-api.md §7) at the JSON boundary: an element the
 * generated types cannot vouch for still has to be placeable; one that is
 * not even placeable is dropped, not guessed at. Runtime shape-checking is
 * deliberately shallow — the visible-line rule is enforced by rendering
 * unknown service kinds, not by rejecting rows. */
function sanitizeMessage(m: Message): Message {
  return m;
}

function stripTag(m: Message & { t?: string }): Message {
  if (!("t" in m)) return m;
  const { t: _tag, ...rest } = m;
  return rest as unknown as Message;
}

function normalizeManual(v: unknown): "dnd" | "invisible" | null {
  return v === "dnd" || v === "invisible" ? v : null;
}

export interface StagedFile {
  file: File;
  /** What the file *is*. Not necessarily what it is sent as — see `asFile`. */
  kind: import("../gen/dto").FileKind;
  /**
   * «Send as file» (D233): upload the original bytes untouched, as a file row
   * with no thumbnail and no preview, instead of the two light weights a
   * picture is normally reduced to. A flag beside `kind` rather than an
   * overwrite of it, because the ceiling that applies is still the picture's.
   */
  asFile?: boolean;
  previewUrl?: string;
  /** Voice notes carry their packed waveform, already base64. */
  waveform?: string;
  /** Voice notes: the recorder measured it; the server has no decoder. */
  durationMs?: number;
  spoiler?: boolean;
}

/** What this attachment travels as, which is its kind unless its sender chose
 * to send a picture as a file (D233). */
function sendKindOf(item: StagedFile): import("../gen/dto").FileKind {
  return item.asFile ? "file" : item.kind;
}

/** The share of a picture's upload progress the thumbnail gets: it is a
 * twentieth of the bytes, and a bar that sat at 50 % for the thumbnail would
 * be lying about which half is the wait. */
const THUMB_SHARE = 0.15;

/** width/height are read BEFORE uploading — the server has no decoder for
 * video and trusts the client (chat-api.md §1.3).
 *
 * Photos do not come here any more: their geometry is whatever the weights
 * came out as (`lib/photo.ts`), and an original sent as a *file* has no
 * geometry to declare — a file row draws no picture. */
async function probeFile(
  item: StagedFile,
  lifetime: SessionLifetime,
): Promise<{ width?: number; height?: number; durationMs?: number }> {
  assertCurrent(lifetime);
  if (item.kind === "video" || item.kind === "video_message") {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(item.file);
      const v = document.createElement("video");
      const cleanup = () => {
        lifetime.signal.removeEventListener("abort", cancel);
        v.onloadedmetadata = null;
        v.onerror = null;
        v.removeAttribute("src");
        v.load();
        URL.revokeObjectURL(url);
      };
      const cancel = () => {
        cleanup();
        reject(lifetime.signal.reason);
      };
      lifetime.signal.addEventListener("abort", cancel, { once: true });
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        const out = {
          width: v.videoWidth,
          height: v.videoHeight,
          durationMs: Math.round(v.duration * 1000),
        };
        cleanup();
        resolve(out);
      };
      v.onerror = () => {
        cleanup();
        resolve({});
      };
      v.src = url;
    });
  }
  if (item.kind === "voice" && item.durationMs !== undefined) {
    return { durationMs: item.durationMs };
  }
  return {};
}
